import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import Input from "@cloudscape-design/components/input"
import Select, { type SelectProps } from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Textarea from "@cloudscape-design/components/textarea"
import React, { useState } from "react"
import type { CreateCommentRequest } from "@/services/dtaCollaborationApi"
import { useCreateComment } from "@/hooks/queries/useCollaboration"

interface CommentFormProps {
  dtaId: string
  vendorCompany: string
  sections: string[]
  version: string
  onCommentCreated?: () => void
}

const CommentForm: React.FC<CommentFormProps> = ({
  dtaId,
  vendorCompany,
  sections,
  version,
  onCommentCreated,
}) => {
  const createComment = useCreateComment(dtaId)
  const [sectionReference, setSectionReference] = useState("General")
  const [commentText, setCommentText] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!commentText.trim()) {
      setError("Please enter a comment")
      return
    }

    setError(null)
    setSuccess(false)

    try {
      const request: CreateCommentRequest = {
        section_reference: sectionReference.trim() || "General",
        comment_text: commentText.trim(),
        vendor_company: vendorCompany,
        version,
      }

      await createComment.mutateAsync(request)

      setSuccess(true)
      setCommentText("")
      setSectionReference("")
      onCommentCreated?.()
      setTimeout(() => setSuccess(false), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create comment")
    }
  }

  const sectionOptions: SelectProps.Option[] = [
    { label: "General", value: "General" },
    ...sections.map((section) => ({
      label: section.replace(/_/g, " ").replace(/\./g, " → "),
      value: section,
    })),
  ]

  const selectedSectionOption =
    sectionOptions.find((opt) => opt.value === sectionReference) ?? sectionOptions[0]

  return (
    <Container variant="stacked" header={<Header variant="h2">Start a Discussion</Header>}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <SpaceBetween size="m">
          <FormField label="Section" description="(optional)">
            {sections.length > 0 ? (
              <Select
                selectedOption={selectedSectionOption}
                onChange={({ detail }) =>
                  setSectionReference(detail.selectedOption.value ?? "General")
                }
                options={sectionOptions}
              />
            ) : (
              <Input
                value={sectionReference}
                onChange={({ detail }) => setSectionReference(detail.value)}
                placeholder="General"
              />
            )}
          </FormField>

          <FormField label="Comment">
            <Textarea
              value={commentText}
              onChange={({ detail }) => setCommentText(detail.value)}
              rows={4}
              placeholder="Enter your comment or question about this section..."
            />
          </FormField>

          {error && <Alert type="error">{error}</Alert>}

          {success && (
            <Alert type="success">
              <Icon name="status-positive" size="normal" /> Comment submitted successfully!
            </Alert>
          )}

          <Box float="right">
            <Button
              variant="primary"
              formAction="submit"
              loading={createComment.isPending}
              disabled={!sectionReference || !commentText.trim()}
            >
              {createComment.isPending ? "Submitting..." : "Start Discussion"}
            </Button>
          </Box>
        </SpaceBetween>
      </form>
    </Container>
  )
}

export default CommentForm
